/**
 * Share-project modal — the façade.
 *
 * SH-5 moved the surface itself into `ui/share/`, so the whole rebuild reverts
 * as one directory deletion. This file keeps the two exports their callers
 * import, and keeps the INVITE-ACCEPT screen, which is a separate landing
 * surface the share/review merge does not touch: different caller
 * (`shell.js` maybeShowInviteAccept), different overlay class, different
 * lifecycle.
 *
 * Built on the component kit and deliberately free of shell.js imports so it
 * stays importable under jsdom for headless tests. All user-provided strings
 * (names, emails) enter the DOM via textContent — never innerHTML.
 */

import { createModal, createButton } from './components.js';
import { describeProjectInvite, acceptProjectInvite, isRealUser } from '../templates/auth-api.js';
import { TemplateApiError } from '../templates/template-api.js';
import { reportError } from '../core/report-error.js';
import { eventBus } from '../core/events.js';
import { showShareSurface } from './share/surface.js';

/**
 * @param {Object} opts
 * @param {string} opts.projectId
 * @param {string} [opts.projectName]
 * @param {{sub:string}|null} opts.currentUser — the signed-in auth payload
 * @returns {{overlay:HTMLElement, close:() => void, ready:Promise<void>}|null}
 */
export function showShareModal(opts) {
  return showShareSurface(opts);
}

/** Invite landing screen. Authentication is requested without consuming the token. */
export function showInviteAcceptModal({ token, currentUser = null, onAccepted, onInvalid } = {}) {
  if (!token || document.querySelector('.pb-invite-accept-overlay')) return null;
  const { overlay, body, footer, close } = createModal({
    title: 'Project invitation',
    overlayClassName: 'pb-invite-accept-overlay',
    withFooter: true,
    maxWidth: 480
  });
  const status = document.createElement('p');
  status.className = 'pb-confirm-msg';
  status.textContent = 'Checking invitation…';
  body.appendChild(status);

  const ready = (async () => {
    try {
      const invite = await describeProjectInvite(token);
      status.textContent = `${invite.invitedBy} invited you to edit “${invite.projectName}”.`;
      if (!isRealUser(currentUser)) {
        footer.appendChild(
          createButton({
            variant: 'primary',
            label: 'Sign in to accept',
            onClick: () => {
              close();
              eventBus.emit('toolbar:auth-show');
            }
          })
        );
        return;
      }
      footer.appendChild(
        createButton({
          variant: 'primary',
          label: 'Accept invitation',
          busyLabel: 'Accepting…',
          errorScope: 'Invite acceptance',
          onAsyncClick: async () => {
            const accepted = await acceptProjectInvite(token);
            close();
            await onAccepted?.(accepted);
          }
        })
      );
    } catch (err) {
      if (err instanceof TemplateApiError && err.status === 404) {
        status.textContent = 'This invitation is invalid, expired, or has already been used.';
        onInvalid?.();
      } else {
        status.textContent = 'Could not load this invitation.';
        reportError('Invite description', err, { user: 'silent' });
      }
    }
  })();
  return { overlay, close, ready };
}
